
import Vue from 'vue'
import { Component, Prop, Watch } from 'vue-property-decorator'
import snarkdown from 'snarkdown'
import {
    initInlineWidget,
    isCalendlyEvent,
    hasCalendly,
} from '@/utils/calendly'
import {
    sendReply,
    sendReplyDiscreetlyWithRetryOnNetworkFail,
    stillHere,
    finished,
    preloadYearInReviewFinishedPage,
} from '@/api/conversation'
import {
    ConversationSpec,
    ConversationItem,
    ConversationDisplayItem,
    Options,
    Say,
    ReceiveText,
    ReceiveOption,
    OptionLabels,
    LABEL_LEFT_NPS_DEFAULT,
    LABEL_LEFT_FIVESTAR_DEFAULT,
    LABEL_RIGHT_NPS_DEFAULT,
    LABEL_RIGHT_FIVESTAR_DEFAULT,
    CardType,
    Option,
} from '@/entities/conversation'
import {
    initialState,
    proceed,
    transitionDisplay,
    setValues,
    setLabels,
    State as ConversationState,
} from './ConversationState'
import { sleep } from '@/utils/async'
import { answerToNPS, answerToFiveScore, Rating } from '@/domain/NPS'
import { init as initConfetti } from '@/animations/confetti'

import ConversationMessage from './components/ConversationMessage.vue'
import ReceiveTextField from './components/ReceiveTextField.vue'
import { interpolateData } from '@/utils/string'
import qs from 'qs'
import { getCDNUrl } from '@/utils/cdn'
import { debounce } from '@/utils/function'
import { defaultCsatOptions } from '@/transforms/conversation'
import { shuffle } from '@/utils/array'

declare global {
    interface Window {
        askNicelyInterface: {
            postMessage: (message: string) => void
        }
        ReactNativeWebView: {
            postMessage: (message: string) => void
        }
        webkit: {
            messageHandlers: {
                askNicelyInterface: {
                    postMessage: (message: string) => void
                }
                ReactNativeWebView: {
                    postMessage: (message: string) => void
                }
            }
        }
    }
}

declare const UIkit

/**
 * This survey can run in two contexts:
 *
 * From email link:
 *    - answer and rating are known at mount time
 *    - initial score is automatically sent to the server on load
 *    - any subsequent question that tries to submit the initial score will be ignored (not displayed)
 *
 * From inapp:
 *    - answer is unknown at load time
 *    - response to lead question sends answer to server
 *    - rating is derived from answer received clientside
 */

const STILL_HERE_SECONDS = 60
const STORAGE_KEY = 'asknicely-conversation'

const calendlyEventWindowSizes = {
    event_type_viewed: '970px',
    date_and_time_selected: '620px',
    event_scheduled: '490px',
}

interface Params {
    [key: string]: string
}

interface SavedData {
    state: ConversationState
    lastSaidText: string
    initialised: boolean
}

@Component<ConversationUIApp>({
    components: {
        ConversationMessage,
        ReceiveTextField,
    },
})
export default class ConversationUIApp extends Vue {
    getCDNUrl = getCDNUrl

    private debouncedReceiveInput = debounce(this.receiveInput, 500, true)

    // expected in params
    public answer: number | null = -1
    public firstInteractiveIndex = -1
    public slug = ''
    public logo = ''
    public template = 'default'
    public version = 1
    public isFiveScore = false
    public inApp = false
    public currentCalendlyFrame
    public disableConfetti = false

    // transient
    public replyText = ''
    public lastSaidText = ''
    public answerCount = 0

    // persisted to localStorage
    public state: ConversationState = initialState([], {})
    public avatar = ''
    public initialised = false // has the splash screen been shown?
    public showNetError = false // has there been a network error?
    public errorTimeout

    // browser tracking
    public hasMouse = false
    public hasKeyBoard = false
    public trackingUrl = ''
    public cardOptionLabels: OptionLabels = {}

    public get rating(): Rating {
        const answer = Number(this.answer)
        return this.isFiveScore
            ? answerToFiveScore(answer)
            : answerToNPS(answer)
    }

    public get storageKey() {
        return `${STORAGE_KEY}/${this.slug}/${this.template}/${this.version}`
    }

    public get itemsWithReplies() {
        return this.state.display.map((item, index) => {
            if (item.cardType !== 'message' && this.firstInteractiveIndex < 0) {
                this.firstInteractiveIndex = index
            }
            if (item.status !== 'new' && item.type === 'Say') {
                /* We need to transfer label text from the card to a variable as the 'Received Option' card, where the labels
               are displayed, don't have this data. */
                if (item.optionLabels) {
                    this.cardOptionLabels = {
                        labelLeft: item.optionLabels?.labelLeft,
                        labelRight: item.optionLabels?.labelRight,
                    }
                }
            }
            // Randomize order of options, if random is enabled and item is new
            if (
                item.status === 'new' &&
                item.type === 'ReceiveOption' &&
                item.options.length &&
                item.randomizeOrder
            ) {
                shuffle(item.options)
            }
            return {
                ...item,
                reply: this.getItemReply(item),
            }
        })
    }

    public labelLeft(item) {
        if (this.isNps(item)) {
            return this.cardOptionLabels.labelLeft
                ? this.cardOptionLabels.labelLeft
                : LABEL_LEFT_NPS_DEFAULT
        }

        if (this.isFivestar(item)) {
            return this.params.labelLeft
                ? this.params.labelLeft
                : LABEL_LEFT_FIVESTAR_DEFAULT
        }

        return null
    }

    public labelRight(item) {
        if (this.isNps(item)) {
            return this.cardOptionLabels.labelRight
                ? this.cardOptionLabels.labelRight
                : LABEL_RIGHT_NPS_DEFAULT
        }

        if (this.isFivestar(item)) {
            return this.params.labelRight
                ? this.params.labelRight
                : LABEL_RIGHT_FIVESTAR_DEFAULT
        }

        return null
    }

    @Prop(Boolean) public preview?: boolean
    @Prop(Object) public params!: Params
    @Prop(Array) public spec!: ConversationSpec
    @Prop(Boolean) public browserTracking!: boolean
    @Prop({ type: Array, default: () => defaultCsatOptions() })
    public csatLabels!: Options

    @Watch('spec')
    public onSpecChange() {
        this.state = initialState(this.spec, this.params) // Reset everything as the conversation has changed

        this.next()
    }

    public async mounted() {
        this.load()

        if (!this.initialised) {
            await sleep(1000)
            this.initialised = true
        }

        if (this.inApp) {
            this.postToParent({
                key: 'conversation_loaded',
            })
        }

        await this.transition()

        // notify server user is still present
        if (!this.preview) {
            const stillHereInterval = setInterval(() => {
                if (!this.state.finished) {
                    stillHere(this.slug, STILL_HERE_SECONDS)
                } else {
                    clearInterval(stillHereInterval)
                }
            }, STILL_HERE_SECONDS * 1000)

            // Preload the Year In Review page so image is generated
            if (this.params.has_christmas === '1' && !this.state.finished) {
                preloadYearInReviewFinishedPage(this.slug)
            }
        }

        // handle any calendly completed
        this.handleCalendlyMessages()

        // send initial score, if not done already and not inApp
        if (this.state.values.initial_score === undefined && !this.inApp) {
            await this.createReply(
                'initial_score',
                String(this.answer),
                String(this.answer),
                'ReceiveText'
            )
        }

        // if not already awaiting input on load, move ahead (only when it's not finished)
        if (
            !this.state.finished &&
            (!this.state.node || this.state.node.type.indexOf('Receive') === -1)
        ) {
            await this.next()
        } else if (this.state.finished && this.params.has_christmas === '1') {
            window.location.href =
                '/email/conversation/' + this.slug + '/finished_year_in_review'
        }
    }

    public isOptionSelected(item, key) {
        return item.options.find((option) => option.key === key)?.isSelected
    }

    public onOptionClicked(
        item,
        value: string,
        label: string,
        badgeId?: string,
        index?: number
    ) {
        if (this.isMultiSelect(item)) {
            const selectOptionIndex = item.options.findIndex(
                (option) => option.key === value
            )
            const selectOption = item.options[selectOptionIndex]

            selectOption.isSelected =
                'isSelected' in selectOption ? !selectOption.isSelected : true

            // item.options are nested object, so to force reactive, we need to use splice function to replace option
            item.options.splice(selectOptionIndex, 1, selectOption)

            return
        }
        debounce(
            this.receiveInput(item, value, label, badgeId, index),
            500,
            true
        )
    }

    public hasSelectedOptions(item) {
        return item.options.some((option) => option.isSelected)
    }

    public onMultiSelectDone(item, index) {
        const selectedOptions = item.options.filter(
            (option) => option.isSelected
        )
        if (selectedOptions.length > 0) {
            const values: string[] = [],
                labels: string[] = [],
                badgeIds: string[] = []

            for (const selectedOption of selectedOptions) {
                values.push(selectedOption.key)
                labels.push(selectedOption.value)
                badgeIds.push(selectedOption.badgeId)
            }

            debounce(
                this.receiveInput(
                    item,
                    values.join(','),
                    labels.join(','),
                    badgeIds.join(','),
                    index,
                    true
                ),
                500,
                true
            )
        }
    }

    public async receiveInput(
        item: ReceiveOption | ReceiveText,
        value: string,
        label: string,
        badgeId?: string,
        index?: number,
        multipleValues?: boolean
    ) {
        this.createReply(
            item.param,
            value,
            label,
            item.type,
            badgeId,
            multipleValues
        )

        if (value === 'yes' && item.link) {
            this.$nextTick(() => {
                this.loadCalendlyFrame(item.link, 'calendly-frame-' + index)
            })
        } else {
            await this.next()
        }
    }

    public async receiveTextInput(item: ReceiveText) {
        if (this.replyText.trim() === '') {
            return
        }
        await this.debouncedReceiveInput(item, this.replyText, this.replyText)
    }

    public getItemText(item: ConversationDisplayItem): string {
        const { state } = this
        let key
        let val
        let reply

        switch (item.type) {
            case 'Say':
                return this.fillParams(item.value)

            case 'ReceiveText':
                key = item.param
                val = state.values[key]
                return val || ''

            case 'ReceiveOption':
                key = item.param
                if (this.isMultiSelect(item)) {
                    val = state.values[key]?.split(',') ?? []
                    const selectedOptions = item.options
                        .filter((opt) => val.includes(opt.key)) // Filter selected value
                        .map((opt) => opt.value) // Get label column values
                    return this.fillParams(
                        selectedOptions.length > 0
                            ? selectedOptions.join(', ')
                            : ''
                    )
                }
                val = state.values[key]
                reply = val && item.options.find((opt) => opt.key === val)
                return this.fillParams(reply ? reply.value : '')
        }
        return ''
    }

    public getItemReply(item: ConversationItem): string | undefined {
        if (item.type === 'ReceiveOption' || item.type === 'ReceiveText') {
            return this.state.values[item.param]
        }
        return undefined
    }

    public getOptions(item: ReceiveOption) {
        let opts = item.options.map(({ key, value, badgeId, isSelected }) => ({
            key,
            value: this.fillParams(value) || '0', // because smarty json_encode parses '0' as ''
            badgeId,
            isSelected,
        }))
        return opts
    }

    // highlight any text before an active Receive, or one with no following item
    public shouldHighlight(idx: number) {
        const { display } = this.state
        const item = display[idx]
        const following = display[idx + 1]
        const states = ['active', 'entering', 'leaving']

        return (
            (item && item.status && states.indexOf(item.status) > -1) ||
            (following
                ? following.type.indexOf('Receive') === 0 &&
                  following.status &&
                  states.indexOf(following.status) > -1
                : true)
        )
    }

    // allow interaction with any widgets before an active Receive
    public shouldInteract(idx: number, firstOnly = false) {
        const { display } = this.state
        const following = display[idx + 1]

        let firstInteractive = this.firstInteractiveIndex
        if (!firstOnly) {
            firstInteractive = idx
        }

        return following
            ? following.type.indexOf('Receive') === 0 &&
                  following.status &&
                  following.status !== 'done' &&
                  idx === firstInteractive
            : false
    }

    public isNps(item: { param?: string; cardType?: string }) {
        return (
            (item.param && !!item.param.match(/_nps$/)) ||
            (item.cardType && item.cardType == 'nps')
        )
    }

    public isFivestar(item: { param?: string; cardType?: string }) {
        return (
            (item.param && !!item.param.match(/_fivestar$/)) ||
            (item.cardType && item.cardType == 'fivestar')
        )
    }

    public isMultiSelect(item) {
        return 'multiSelect' in item && item.multiSelect
    }

    public get referrerUrl() {
        return this.params.referrerUrl
    }

    public get nobranding() {
        return this.params.nobranding
    }

    public loadCalendlyFrame(link, elementId) {
        if (!hasCalendly(this.spec)) {
            return
        }

        this.currentCalendlyFrame = document.getElementById(elementId)

        initInlineWidget(link + '?hide_event_type_details=1', elementId)
    }

    public handleCalendlyMessages() {
        if (!hasCalendly(this.spec)) {
            return
        }

        window.addEventListener('message', (e) => {
            if (isCalendlyEvent(e)) {
                this.currentCalendlyFrame.scrollIntoView({
                    behavior: 'smooth',
                    block: 'start',
                })
                const event = e.data.event.substring(9)
                if (calendlyEventWindowSizes[event] !== undefined) {
                    this.currentCalendlyFrame.style.height =
                        calendlyEventWindowSizes[event]
                }
                if (e.data.event === 'calendly.event_scheduled') {
                    this.next(true)
                }
            }
        })
    }

    public handleMouseMove() {
        this.hasMouse = true
        this.addTrackingImage()
    }

    public handleKeyDown() {
        this.hasKeyBoard = true
        this.addTrackingImage()
    }

    public addTrackingImage() {
        if (!this.browserTracking) {
            return false
        }
        const resolution = window.screen.width + '*' + window.screen.height

        const params: { [key: string]: any } = {}
        params.res = resolution
        if (this.hasMouse) {
            params.mouse = 1
        }
        if (this.hasKeyBoard) {
            params.keyboard = 1
        }

        this.trackingUrl = `/email/trackbrowser/${this.slug}?${qs.stringify(
            params
        )}`
    }

    private isLocalStorageAvailable() {
        try {
            localStorage.getItem(this.storageKey)
        } catch {
            return false
        }

        return true
    }

    private load() {
        // parse initial data from template
        const spec = this.spec
        const params = this.params

        this.avatar = params.avatar
        const {
            slug,
            logo,
            answer,
            inapp,
            isFiveScore,
            template,
            version,
            disableConfetti,
        } = params
        this.slug = slug
        this.logo = logo
        this.template = template
        this.version = parseInt(version, 10)
        this.answer = /^\d+$/.test(answer) ? parseInt(answer, 10) : null
        this.isFiveScore = !!isFiveScore
        this.inApp = !!inapp
        this.disableConfetti = !!disableConfetti
        this.answerCount = 0

        // try get data from local storage
        const saved =
            !this.preview &&
            this.isLocalStorageAvailable() &&
            localStorage.getItem(this.storageKey)

        if (saved) {
            const { state, lastSaidText, initialised } = JSON.parse(
                saved
            ) as SavedData

            if (
                this.answer &&
                !(parseInt(state.values.answer) == this.answer)
            ) {
                this.updateScore({ score: this.answer }).then(() => {
                    this.state = state
                    this.lastSaidText = lastSaidText
                    this.initialised = initialised
                })
            } else {
                this.state = state
                this.lastSaidText = lastSaidText
                this.initialised = initialised
            }

            // construct initial state manager and save
        } else {
            this.state = initialState(spec, params)

            this.save()
        }
    }

    private save() {
        if (this.preview || !this.isLocalStorageAvailable()) {
            return
        }
        const data: SavedData = {
            state: this.state,
            lastSaidText: this.lastSaidText,
            initialised: this.initialised,
        }
        localStorage.setItem(this.storageKey, JSON.stringify(data))
    }

    private createReply(
        key: string,
        value: string,
        label: string,
        inputType: string,
        badgeId?: string,
        multipleValues?: boolean
    ) {
        if (value.toString().trim() === '') {
            return
        }

        const newValues = { [key]: value }
        const newLabels = { [key]: label ?? value }

        // if in app and it's the lead question,
        // also load it into scope and derive rating
        // and notify parent window
        if (
            this.inApp &&
            this.isAnswerKey(key) &&
            !this.state.values.hasOwnProperty('initial_score')
        ) {
            const answer = Number(value)
            this.answer = answer
            newValues.answer = value
            newValues.rating = this.rating
        }

        if (this.inApp && this.answerCount === 0) {
            this.postToParent({
                key: 'initial_score',
            })
        }
        this.answerCount++

        this.replyText = ''
        this.state = setValues(this.state, newValues) // set locally
        this.state = setLabels(this.state, newLabels)

        if (!this.preview) {
            sendReplyDiscreetlyWithRetryOnNetworkFail(this.slug, {
                // don't wait for response - UI is optimistic with no undo on fail
                key,
                value,
                badgeId,
                inputType,
                questionText: this.lastSaidText,
                template: this.template,
                version: this.version,
                answer: this.answer,
                multipleValues,
            }) // send to server
        }
        this.save()
    }

    private async updateScore(data) {
        const key = 'update_score'
        const value = String(data.score)

        let response = {}
        let url = new URL(location.href)

        // Handle "preview" score changes. Since we do not have a database entry for preview,
        // we just reload the page with the new score change
        if (url.pathname.includes('email/conversation/preview')) {
            let path = location.href
            window.location.href =
                path.substr(0, path.lastIndexOf('/') + 1) + value // Replace old score with new score.
            return
        }

        try {
            response = await sendReply(this.slug, {
                // we just updated the initial score, wait for result
                key,
                value,
                badgeId: undefined,
                inputType: 'ReceiveText',
                questionText: this.lastSaidText,
                template: this.template,
                version: this.version,
            })
        } catch (error) {
            this.showNetError = true
            if (this.errorTimeout) {
                clearTimeout(this.errorTimeout)
            }
            this.errorTimeout = setTimeout(() => {
                this.showNetError = false
            }, 1000)

            return
        }

        this.answer = Number(value)

        const newValues = {
            [key]: value,
            answer: this.answer ? this.answer.toString() : '0',
            rating: this.isFiveScore
                ? answerToFiveScore(this.answer)
                : answerToNPS(this.answer),
        }

        this.answerCount = 0

        this.save()

        this.$emit('updatespec', { answer: newValues.answer, spec: response })

        this.state = setValues(this.state, newValues) // Getting the new values set
    }

    private alert(message: string, status?: string) {
        UIkit.notify(message, {
            status,
            timeout: 3000,
            pos: 'top-center',
        })
    }

    // List of question types that can be used for the inital score. (missing 5 star)
    private isAnswerKey(key: string) {
        return (
            [
                'initial_score',
                'score_nps',
                'answer',
                'answer_nps',
                'answer_csat',
            ].indexOf(key) > -1
        )
    }

    private async transition() {
        const SECOND_BOX = 1
        for await (const next of transitionDisplay(this.state)) {
            this.state = next
        }

        if (
            this.state.display[this.state.display.length - 1] !== undefined &&
            this.state.display[this.state.display.length - 1].status ===
                'active' &&
            'getBoundingClientRect' in this.$refs.container &&
            this.$refs.container.children.length >= 3
        ) {
            const children = this.$refs.container.children
            const top =
                children[children.length - 2].getBoundingClientRect().top
            const bottom =
                children[children.length - 1].getBoundingClientRect().bottom
            const paddingY = 100 + (children.length > 3 ? 80 : 0)
            const height = bottom - top + paddingY
            this.postToParent({
                key: 'set_height',
                value: height,
            })
        }
        this.save()
    }

    private async next(afterCalendly = false) {
        // move the conversation forward
        this.state = proceed(this.state)
        this.save()

        if (this.state.finished) {
            this.scrollToCurrent(afterCalendly)

            if (!this.preview) {
                await finished(this.slug)
            }

            await sleep(600)

            const showConfetti =
                !this.disableConfetti && this.rating === 'promoter'
            if (showConfetti) {
                await this.throwConfetti()
            } else {
                await sleep(600)
            }

            this.postToOS('askNicelyDone', 'Success')

            this.postToParent({
                key: 'finished',
                value: this.lastSaidText,
                showConfetti,
            })

            // Christmas redirect
            if (this.params.has_christmas === '1') {
                window.location.href =
                    '/email/conversation/' +
                    this.slug +
                    '/finished_year_in_review'
            }

            return
        }

        await this.transition()

        const { node } = this.state

        // if item is a 'say', immediately go to next. otherwise wait for user input (skip or reply)
        if (!node) {
            return
        } else if (node.type === 'Say') {
            this.lastSaidText = this.getItemText(
                node as ConversationDisplayItem
            )
            await this.next(afterCalendly)
        } else {
            this.scrollToCurrent(afterCalendly)
            await this.save()
        }
    }

    // @todo skip()

    private scrollToCurrent(afterCalendly = false) {
        const agentMessages = document.getElementsByClassName('agent-message')
        const prev = agentMessages[agentMessages.length - 2] // use prev to provide some offset

        if (
            prev &&
            !afterCalendly &&
            !this.isInapp() &&
            !window.parent.document.getElementById('conversational')
        ) {
            prev.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }
        if (prev && this.isInapp()) {
            const current = agentMessages[agentMessages.length - 1]
            setTimeout(
                () =>
                    current.scrollIntoView({
                        behavior: 'smooth',
                        block: 'start',
                    }),
                250
            )
        }
    }

    private isInapp() {
        const parameters = qs.parse(window.location.search, {
            ignoreQueryPrefix: true,
        })

        return (
            parameters.hasOwnProperty('inapp') ||
            (parameters.template_name && parameters.template_name === 'inapp')
        )
    }

    private fillParams(str: string): string {
        const { values, labels } = this.state
        const replaced = interpolateData(
            str,
            { ...values, ...labels },
            this.params.questionType,
            (value) => {
                return `<ReselectScore v-bind:score=${value} v-bind:questionType="'${this.params.questionType}'" v-bind:interact="interact" v-bind:csatLabels="csatLabels" @updatescore="updateScore"></ReselectScore>`
            },
            this.csatLabels
        )

        return snarkdown(replaced.replace(/\n/gi, '<br/>').trim()) // Replace New Lines and trim.
    }

    private async throwConfetti() {
        const W = window.outerWidth
        const H = window.outerHeight

        const canvas = document.createElement('canvas')
        document.body.appendChild(canvas)
        canvas.style.top = '100px'
        canvas.style.pointerEvents = 'none' // confentti should be non blocking and click through
        await initConfetti(30, canvas)
        document.body.removeChild(canvas)
    }

    private postToParent(
        data: { key: string; value?: any; showConfetti?: boolean },
        targetOrigin = '*'
    ) {
        window.parent.postMessage(data, targetOrigin)
    }

    private postToOS(event, msg) {
        let data = JSON.stringify({ event: event, msg: msg })
        if (window.webkit != undefined) {
            if (window.webkit.messageHandlers.askNicelyInterface != undefined) {
                window.webkit.messageHandlers.askNicelyInterface.postMessage(
                    data
                )
            }
            if (window.webkit.messageHandlers.ReactNativeWebView != undefined) {
                window.webkit.messageHandlers.ReactNativeWebView.postMessage(
                    data
                )
            }
        }
        if (window.askNicelyInterface != undefined) {
            window.askNicelyInterface.postMessage(data)
        }
        if (window.ReactNativeWebView != undefined) {
            window.ReactNativeWebView.postMessage(data)
        }
    }
}
